import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Plus,
  FileText,
  RefreshCw,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { 
  getPrescriptions,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { Prescription } from '@/types/prescription';
import PrintPrescriptionPreviewModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import PrescriptionDataTable from '@/components/Prescription/PrescriptionDataTable';
import SimpleFilters, { type SimpleFilterState } from '@/components/Prescription/SimpleFilters';
import PatientSearchInput from '@/components/shared/PatientSearchInput';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

export default function PrescriptionsPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => { document.title = "Prescriptions | OneCheckUp"; }, []);

  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Inline patient search for new prescription
  const [showPatientSearch, setShowPatientSearch] = useState<boolean>(false);
  const [patientSearchText, setPatientSearchText] = useState<string>('');

  // Simple filter states
  const [simpleFilters, setSimpleFilters] = useState<SimpleFilterState>({
    searchQuery: '',
    dateFilter: 'all',
    dateRange: { from: null, to: null },
    selectedMedicines: [],
    selectedComplaints: [],
    selectedPatients: []
  });


  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // Print modal state
  const [showPrintModal, setShowPrintModal] = useState<boolean>(false);
  const [prescriptionToPrint, setPrescriptionToPrint] = useState<Prescription | null>(null);

  const { currentUser } = useAuth();

  // Handle navigation state and URL parameters
  useEffect(() => {
    const handleNavigationContext = () => {
      // Handle URL search parameters
      const filterParam = searchParams.get('filter');
      const patientParam = searchParams.get('patient');
      const modeParam = searchParams.get('mode');
      
      // Handle navigation state
      const state = location.state;
      
      // Set filter based on URL parameter - use simple filters
      if (filterParam === 'today') {
        setSimpleFilters(prev => ({
          ...prev,
          dateFilter: 'today'
        }));
      }
      
      // Set filter based on navigation state
      if (state?.filter === 'patient' && state?.patientId) {
        // When coming from patient-specific view, focus on that patient
        const patientId = state.patientId;
        const patientName = state.patientName;
        if (patientName) {
          setSimpleFilters(prev => ({
            ...prev,
            searchQuery: patientName
          }));
        }
      }
      
      // Handle mode-specific behavior
      if (state?.mode === 'create' || modeParam === 'consultation') {
        // Could trigger create prescription modal/flow here
        // console.log('Create prescription mode activated');
      }
      
      // Handle focus search from dashboard
      if (state?.focusSearch) {
        // Focus the search input when component mounts
        setTimeout(() => {
          const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
          }
        }, 100);
      }
    };

    handleNavigationContext();
  }, [searchParams, location.state]);

  const loadData = useCallback(async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getPrescriptions({ doctor_id: currentUser.doctor_id, clinic_id: currentUser.clinic_id });
      if (data) setPrescriptions(data);
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load prescriptions. Please try again.';
      setError(errorMessage);
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [currentUser?.doctor_id, currentUser?.clinic_id]);

  // Load initial data (after loadData is declared)
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadData();
      toast({ title: "Success", description: "Data refreshed successfully" });
    } catch {
      toast({ title: "Error", description: "Failed to refresh data", variant: "destructive" });
    } finally {
      setRefreshing(false);
    }
  };

  const parsePrescriptionData = useCallback((prescription: Prescription) => ({
    ...prescription,
    patientName: prescription.patient_name || 'Unknown Patient',
    complaints: prescription.complaints || [],
    medicines: prescription.medicines || [],
    investigations: prescription.investigations || [],
    vitals: prescription.vitals || {},
    next_visit: prescription.next_visit || null,
    advice: prescription.advice || '',
    diagnosis: prescription.diagnosis || '',
    quickNotes: prescription.quick_notes || '',
  }), []);

  // Derive patient list from loaded prescriptions — no bulk getPatients() needed
  const patients = useMemo(() => {
    const seen = new Set<string>();
    return prescriptions.reduce<{ id: string; name: string }[]>((acc, p) => {
      if (p.patient_id && !seen.has(p.patient_id)) {
        seen.add(p.patient_id);
        acc.push({ id: p.patient_id, name: p.patient_name || '' });
      }
      return acc;
    }, []);
  }, [prescriptions]);

  // Enhanced filtering with simple filters
  const simpleFilteredPrescriptions = useMemo(() => (prescriptions || [])
    .map(parsePrescriptionData)
    .filter((prescription) => {
      // Search filter
      const matchesSearch = !simpleFilters.searchQuery ||
        prescription.patientName?.toLowerCase().includes(simpleFilters.searchQuery.toLowerCase()) ||
        prescription.id?.toLowerCase().includes(simpleFilters.searchQuery.toLowerCase());

      // Date filter logic
      let matchesDate = true;
      const prescriptionDate = new Date(prescription.created_at || prescription.updated_at || '');
      
      if (simpleFilters.dateFilter !== 'all') {
        const now = new Date();
        const prescriptionDateOnly = new Date(prescriptionDate.getFullYear(), prescriptionDate.getMonth(), prescriptionDate.getDate());
        
        if (simpleFilters.dateFilter === 'today') {
          const todayOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          matchesDate = prescriptionDateOnly.getTime() === todayOnly.getTime();
        } else if (simpleFilters.dateFilter === 'week') {
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          const weekStartOnly = new Date(sevenDaysAgo.getFullYear(), sevenDaysAgo.getMonth(), sevenDaysAgo.getDate());
          matchesDate = prescriptionDateOnly >= weekStartOnly;
        } else if (simpleFilters.dateFilter === 'month') {
          const monthStartOnly = new Date(now.getFullYear(), now.getMonth(), 1);
          matchesDate = prescriptionDateOnly >= monthStartOnly;
        }
      }

      // Custom date range filter (overrides quick date filter)
      if (simpleFilters.dateRange?.from || simpleFilters.dateRange?.to) {
        const prescriptionDateOnly = new Date(prescriptionDate.getFullYear(), prescriptionDate.getMonth(), prescriptionDate.getDate());
        
        if (simpleFilters.dateRange.from && simpleFilters.dateRange.to) {
          const fromDate = new Date(simpleFilters.dateRange.from.getFullYear(), simpleFilters.dateRange.from.getMonth(), simpleFilters.dateRange.from.getDate());
          const toDate = new Date(simpleFilters.dateRange.to.getFullYear(), simpleFilters.dateRange.to.getMonth(), simpleFilters.dateRange.to.getDate());
          matchesDate = prescriptionDateOnly >= fromDate && prescriptionDateOnly <= toDate;
        } else if (simpleFilters.dateRange.from) {
          const fromDate = new Date(simpleFilters.dateRange.from.getFullYear(), simpleFilters.dateRange.from.getMonth(), simpleFilters.dateRange.from.getDate());
          matchesDate = prescriptionDateOnly >= fromDate;
        } else if (simpleFilters.dateRange.to) {
          const toDate = new Date(simpleFilters.dateRange.to.getFullYear(), simpleFilters.dateRange.to.getMonth(), simpleFilters.dateRange.to.getDate());
          matchesDate = prescriptionDateOnly <= toDate;
        }
      }

      // Medicine filter - medicines are now properly structured objects
      const matchesMedicines = simpleFilters.selectedMedicines.length === 0 ||
        prescription.medicines?.some(med => 
          simpleFilters.selectedMedicines.includes(med.product || '')
        );

      // Complaint filter - complaints are now properly structured objects
      const matchesComplaints = simpleFilters.selectedComplaints.length === 0 ||
        prescription.complaints?.some(complaint => 
          simpleFilters.selectedComplaints.includes(complaint.complaint || '')
        );

      // Patient filter
      const matchesPatients = simpleFilters.selectedPatients.length === 0 ||
        simpleFilters.selectedPatients.includes(prescription.patientName || '');

      return matchesSearch &&
             matchesDate && matchesMedicines && matchesComplaints && matchesPatients;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at || a.updated_at).getTime();
      const dateB = new Date(b.created_at || b.updated_at).getTime();
      return dateB - dateA;
    })
  , [prescriptions, simpleFilters, parsePrescriptionData]);

  const filteredPrescriptions = simpleFilteredPrescriptions;

  // Sync totalItems and reset page when filtered list shrinks
  useEffect(() => {
    setTotalItems(filteredPrescriptions.length);
    const maxPage = Math.ceil(filteredPrescriptions.length / itemsPerPage);
    if (currentPage > maxPage && maxPage > 0) setCurrentPage(1);
  }, [filteredPrescriptions.length, itemsPerPage, currentPage]);

  // Get paginated data
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPrescriptions = filteredPrescriptions.slice(startIndex, endIndex);

  // Pagination calculations
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : startIndex + 1;
  const endItem = Math.min(startIndex + itemsPerPage, totalItems);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Reset pagination when simple filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [simpleFilters]);



  const handleViewPrescription = (prescription: Prescription) => {
    setPrescriptionToPrint(prescription);
    setShowPrintModal(true);
  };

  const handleEditPrescription = (prescription: Prescription) => {
    const date = prescription.date ? new Date(prescription.date) : null;
    const today = new Date();
    const isToday = date &&
      date.getFullYear() === today.getFullYear() &&
      date.getMonth()    === today.getMonth()    &&
      date.getDate()     === today.getDate();

    if (!isToday) {
      toast({ title: "Cannot Edit", description: "Only today's prescriptions can be edited.", variant: "destructive" });
      return;
    }
    navigate(`/prescriptions/edit/${prescription.id}`);
  };

  const handlePrintPrescription = (prescription: Prescription) => {
    setPrescriptionToPrint(prescription);
    setShowPrintModal(true);
  };



  // Simple filter handlers
  const handleFiltersChange = (filters: SimpleFilterState) => {
    setSimpleFilters(filters);
    setCurrentPage(1); // Reset pagination when filters change
  };










  if (loading) {
    return (
      <div className="prescription-form-container">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading prescriptions...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prescription-form-container">
        <div className="medical-card p-8 text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Failed to Load Prescriptions</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="prescription-form-container">
      {/* Enterprise Header */}
      <div className="border-b border-border pb-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-xl">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Prescriptions</h1>
              <p className="text-sm text-muted-foreground">Manage and track patient prescriptions</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              className="h-9"
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
              Refresh
            </Button>
            
            {showPatientSearch ? (
              <div className="w-72">
                <PatientSearchInput
                  value={patientSearchText}
                  onChange={setPatientSearchText}
                  onSelect={(patient) => {
                    setShowPatientSearch(false);
                    setPatientSearchText('');
                    navigate(`/patients/${patient.id}?openPrescription=true`);
                  }}
                  onEscape={() => { setShowPatientSearch(false); setPatientSearchText(''); }}
                  onBlur={() => { setShowPatientSearch(false); setPatientSearchText(''); }}
                  placeholder="Search patient for new prescription…"
                  inputSize="sm"
                  className="h-9"
                  containerClassName="w-full"
                  autoFocus
                />
              </div>
            ) : (
              <Button
                onClick={() => setShowPatientSearch(true)}
                size="sm"
                className="h-9"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Prescription
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Prescription Management Content */}
      <div className="space-y-4">

        {/* Simple Search & Filters */}
        <SimpleFilters
          prescriptions={prescriptions}
          patients={patients}
          onFiltersChange={handleFiltersChange}
          totalResults={filteredPrescriptions.length}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
        />



        {/* Simple Data Table */}
        <PrescriptionDataTable
          prescriptions={paginatedPrescriptions}
          patients={patients}
          loading={loading}
          onView={handleViewPrescription}
          onEdit={handleEditPrescription}
          onPrint={handlePrintPrescription}

          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          totalItems={filteredPrescriptions.length}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          hideSearchAndFilters={true}
        />
      </div>

      {/* Print / View Prescription Modal */}
      <PrintPrescriptionPreviewModal
        open={showPrintModal}
        prescription={prescriptionToPrint}
        onClose={() => {
          setShowPrintModal(false);
          setPrescriptionToPrint(null);
        }}
      />

    </div>
  );
}